import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type CenterInfo } from '../api';
import { useAuth } from '../auth/AuthContext';
import { Alert, Chip, Dialog, Empty, Field, Loading, PageHeader, useLoad } from '../components/ui';
import { MODULE_LABEL, PLAN_LABEL } from '../format';

const STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'danger' }> = { PAID: { label: '유료계약', tone: 'ok' }, TRIAL: { label: '무료체험', tone: 'warn' }, SUSPENDED: { label: '일시정지', tone: 'danger' } };

/** 본사: 센터 목록과 대리접속(사유 필수, 기본 읽기 전용, 만료 시간 있음). */
export function Hq() {
  const { startImpersonation } = useAuth();
  const nav = useNavigate();
  const centers = useLoad(() => api.hqCenters(), []);
  // 처리 대기 건수(참고: 클래스업 대시보드, docs/UI_REFERENCES.md). 하나가 실패해도 센터 목록은 보인다.
  const queue = useLoad(async () => {
    const [c, m, i] = await Promise.all([api.hqContracts().catch(() => null), api.hqMessaging().catch(() => null), api.hqInquiries().catch(() => null)]);
    return {
      contracts: c ? c.contracts.filter((x) => x.status === 'PENDING').length : null,
      messaging: m ? m.requests.filter((x) => x.status === 'PENDING').length : null,
      inquiries: i ? i.inquiries.filter((x) => x.status === 'OPEN').length : null,
    };
  }, []);
  const list = centers.data?.centers ?? [];
  const [target, setTarget] = useState<CenterInfo | null>(null);
  const [form, setForm] = useState({ mode: 'READ' as 'READ' | 'WRITE', reason: '', ticketRef: '' });

  return (
    <>
      <PageHeader title="센터 관리" />
      <div className="kpis home-kpis">
        <div className="kpi"><span>가맹 센터</span><strong>{centers.data ? `${list.length}곳` : '–'}</strong>
          <small>{centers.data ? `유료 ${list.filter((c) => c.status === 'PAID').length} · 무료체험 ${list.filter((c) => c.status === 'TRIAL').length} · 일시정지 ${list.filter((c) => c.status === 'SUSPENDED').length}` : '불러오는 중'}</small></div>
        <QueueCard to="/hq/contracts" label="계약 심사 대기" n={queue.data?.contracts} unit="건" />
        <QueueCard to="/hq/messaging" label="알림톡 승인 대기" n={queue.data?.messaging} unit="건" />
        <QueueCard to="/hq/notices" label="답변할 문의" n={queue.data?.inquiries} unit="건" />
      </div>
      {centers.error && <Alert>{centers.error}</Alert>}
      {centers.loading && !centers.data && <Loading />}
      {centers.data?.centers.length === 0 && <Empty>등록된 센터가 없습니다.</Empty>}
      {centers.data && centers.data.centers.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">가맹 센터 목록</caption>
            <thead><tr><th scope="col">기관코드</th><th scope="col">센터명</th><th scope="col">계약</th><th scope="col">요금제</th><th scope="col">모듈</th><th scope="col">센터 화면</th></tr></thead>
            <tbody>
              {centers.data.centers.map((c) => (
                <tr key={c.id}>
                  <td className="mono">{c.id}</td>
                  <td><strong>{c.name}</strong></td>
                  <td><Chip tone={STATUS[c.status]?.tone ?? 'warn'}>{STATUS[c.status]?.label ?? c.status}</Chip></td>
                  <td>{PLAN_LABEL[c.plan] ?? c.plan}</td>
                  <td className="small">{c.modules.map((m) => MODULE_LABEL[m] ?? m).join(' · ')}</td>
                  <td><button type="button" className="btn btn-outline btn-sm" onClick={() => { setForm({ mode: 'READ', reason: '', ticketRef: '' }); setTarget(c); }} aria-label={`${c.name} 센터 화면 열기`}>센터 화면 열기</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog title={`${target?.name ?? ''} 센터 화면 열기`} open={Boolean(target)} onClose={() => setTarget(null)} submitLabel="사유 남기고 열기"
        onSubmit={async () => {
          const { session } = await api.startImpersonation({ centerId: target!.id, mode: form.mode, reason: form.reason, ticketRef: form.ticketRef || undefined });
          await startImpersonation(session);
          nav('/home');
        }}>
        <fieldset className="radios">
          <legend>접속 방식</legend>
          <label><input type="radio" name="mode" checked={form.mode === 'READ'} onChange={() => setForm({ ...form, mode: 'READ' })} /> 읽기 전용 (30분)</label>
          <label><input type="radio" name="mode" checked={form.mode === 'WRITE'} onChange={() => setForm({ ...form, mode: 'WRITE' })} /> 쓰기 허용 (15분, 문의 번호 필요)</label>
        </fieldset>
        <Field id="imp-reason" label="접속 사유" hint="예: 수납 금액 불일치 문의 확인 (5자 이상)">
          <input id="imp-reason" required minLength={5} maxLength={300} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} aria-describedby="imp-reason-hint" />
        </Field>
        {form.mode === 'WRITE' && (
          <Field id="ticket" label="문의·장애 번호"><input id="ticket" required maxLength={60} value={form.ticketRef} onChange={(e) => setForm({ ...form, ticketRef: e.target.value })} /></Field>
        )}
        <p className="hint">민감정보 열람과 개인정보 다운로드는 대리접속 중에는 할 수 없습니다.</p>
      </Dialog>
    </>
  );
}

/** 처리 대기 카드: 누르면 해당 화면, 대기가 있으면 주황 테두리. 못 불러오면 '–' */
function QueueCard({ to, label, n, unit }: { to: string; label: string; n: number | null | undefined; unit: string }) {
  return (
    <Link to={to} className={`kpi kpi-link${n ? ' kpi-warn' : ''}`}>
      <span>{label}</span>
      <strong>{n === null || n === undefined ? '–' : `${n}${unit}`}</strong>
      <small>{n === null ? '불러오지 못했어요 · 눌러서 확인' : n === undefined ? '불러오는 중' : n ? '눌러서 처리하기' : '대기 없음'}</small>
    </Link>
  );
}
